import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

export default function UserHome() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ projects: 0, requirements: 0, testCases: 0, testRuns: 0 });
  const [jiraConnected, setJiraConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/projects').catch(() => ({ data: { data: [] } })),
      api.get('/jira/global-config').catch(() => ({ data: {} })),
    ]).then(([projRes, jiraRes]) => {
      const projs = projRes.data?.data || [];
      setProjects(projs.slice(0, 6));
      setStats(s => ({ ...s, projects: projs.length }));
      setJiraConnected(!!(jiraRes.data?.lastTestedOk || jiraRes.data?.envConfigured));
    }).finally(() => setLoading(false));
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const quickLinks = [
    { icon: '🏗️', label: 'Projects', desc: 'View and manage all projects', to: '/projects', color: '#4f46e5' },
    { icon: '🔗', label: 'JIRA Integration', desc: jiraConnected ? '● Connected' : '○ Not connected', to: '/jira', color: jiraConnected ? '#16a34a' : '#d97706' },
    { icon: '⚙️', label: 'Global Settings', desc: 'Integrations and system config', to: '/settings', color: '#475569' },
  ];

  return (
    <div>
      <div className="page-header" style={{ paddingBottom: 24 }}>
        <div>
          <h1 className="page-title" style={{ fontSize: 26 }}>
            {greeting}, {user.username || 'there'} 👋
          </h1>
          <p className="page-subtitle">Here's what's happening in your workspace.</p>
        </div>
      </div>

      <div className="container-fluid">
        {/* Quick access cards */}
        <div className="row g-3 mb-4">
          {quickLinks.map(q => (
            <div className="col-md-4" key={q.to}>
              <Link to={q.to} style={{ textDecoration: 'none' }}>
                <div className="card h-100" style={{
                  borderLeft: `4px solid ${q.color}`,
                  transition: 'box-shadow .15s, transform .15s',
                  cursor: 'pointer',
                }}
                  onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.1)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                >
                  <div className="card-body d-flex align-items-center gap-3">
                    <span style={{ fontSize: 32 }}>{q.icon}</span>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 15, color: 'var(--zq-text)' }}>{q.label}</div>
                      <div style={{ fontSize: 12, color: q.label === 'JIRA Integration' ? q.color : 'var(--zq-text-muted)' }}>{q.desc}</div>
                    </div>
                    <span style={{ marginLeft: 'auto', color: 'var(--zq-text-muted)', fontSize: 18 }}>›</span>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>

        {/* Recent projects */}
        <div className="section-header">
          <span>Recent Projects</span>
          <Link to="/projects" className="btn btn-sm btn-outline-primary">View all →</Link>
        </div>

        {loading ? (
          <div className="zq-spinner-wrap">
            <span className="spinner-border" style={{ color: 'var(--zq-primary)' }} />
          </div>
        ) : projects.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: 40 }}>🏗️</div>
            <h5>No projects yet</h5>
            <p className="text-muted">Create your first project to get started.</p>
            <Link to="/projects" className="btn btn-primary">Create Project</Link>
          </div>
        ) : (
          <div className="row g-3">
            {projects.map(p => {
              const initials = (p.key || p.name || '?').slice(0, 2).toUpperCase();
              const statusColor = p.status === 'active' ? '#16a34a' : p.status === 'completed' ? '#2563eb' : '#6b7280';
              return (
                <div className="col-md-4" key={p._id || p.id}>
                  <Link to={`/projects/${p._id || p.id}`} style={{ textDecoration: 'none' }}>
                    <div className="card h-100" style={{ cursor: 'pointer', transition: 'box-shadow .15s, transform .15s' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,.1)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                    >
                      <div className="card-body d-flex align-items-start gap-3">
                        <div style={{
                          width: 40, height: 40, borderRadius: 8, flexShrink: 0,
                          background: 'var(--zq-primary-light, #ede9fe)',
                          color: 'var(--zq-primary)', fontWeight: 800, fontSize: 13,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{initials}</div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--zq-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</div>
                          <div style={{ fontSize: 12, color: 'var(--zq-text-muted)' }}>{p.key}</div>
                          <span style={{ fontSize: 11, color: statusColor, fontWeight: 600, textTransform: 'capitalize' }}>● {p.status || 'active'}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
            {stats.projects > 6 && (
              <div className="col-md-4">
                <Link to="/projects" style={{ textDecoration: 'none' }}>
                  <div className="card h-100 d-flex align-items-center justify-content-center" style={{ minHeight: 80, cursor: 'pointer', borderStyle: 'dashed' }}>
                    <span style={{ color: 'var(--zq-text-muted)', fontSize: 13 }}>+{stats.projects - 6} more projects →</span>
                  </div>
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
